import { useTranslation } from "react-i18next";
import { Play, RotateCcw } from "lucide-react";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { cn } from "../../lib/utils";

export type AutoPlayMode = "single" | "always";

export interface AutoPlayConfig {
  enabled: boolean;
  mode: AutoPlayMode;
}

interface AutoPlaySettingsProps {
  config: AutoPlayConfig;
  onChange: (config: AutoPlayConfig) => void;
  disabled?: boolean;
  compact?: boolean;
}

export function AutoPlaySettings({
  config,
  onChange,
  disabled = false,
  compact = false,
}: AutoPlaySettingsProps) {
  const { t } = useTranslation();

  const handleEnabledChange = (enabled: boolean) => {
    onChange({ ...config, enabled });
  };

  const handleModeChange = (mode: AutoPlayMode) => {
    onChange({ ...config, mode });
  };

  return (
    <div
      className={cn("space-y-3", disabled && "opacity-50 pointer-events-none")}
    >
      {/* Enable toggle - dir="ltr" on switch so on/off direction stays same in RTL */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Play className="w-4 h-4 shrink-0 text-muted-foreground" />
          <Label
            htmlFor="autoplay-toggle"
            className={cn(
              "text-sm font-medium cursor-pointer",
              compact && "text-xs"
            )}
          >
            {t("autoplay_on_entry", "Auto-play on entry")}
          </Label>
        </div>
        <span dir="ltr" className="shrink-0">
          <Switch
            id="autoplay-toggle"
            checked={config.enabled}
            onCheckedChange={handleEnabledChange}
            disabled={disabled}
          />
        </span>
      </div>

      {/* Mode selector - only show when enabled; use logical padding for RTL */}
      {config.enabled && (
        <div className={cn("ps-6", compact && "ps-4")}>
          <RadioGroup
            value={config.mode}
            onValueChange={(value) => handleModeChange(value as AutoPlayMode)}
            className="space-y-2"
            disabled={disabled}
          >
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="single"
                id="mode-single"
                className="mt-0.5"
              />
              <div className="flex-1">
                <Label
                  htmlFor="mode-single"
                  className={cn(
                    "text-sm font-medium cursor-pointer",
                    compact && "text-xs"
                  )}
                >
                  {t("autoplay_once", "Play once")}
                </Label>
                <p
                  className={cn(
                    "text-xs text-muted-foreground",
                    compact && "text-[10px]"
                  )}
                >
                  {t(
                    "autoplay_once_description",
                    "Only plays the first time user enters this pane"
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <RadioGroupItem
                value="always"
                id="mode-always"
                className="mt-0.5"
              />
              <div className="flex-1">
                <Label
                  htmlFor="mode-always"
                  className={cn(
                    "text-sm font-medium cursor-pointer flex items-center gap-1",
                    compact && "text-xs"
                  )}
                >
                  <RotateCcw className="w-3 h-3" />
                  {t("autoplay_always", "Play every time")}
                </Label>
                <p
                  className={cn(
                    "text-xs text-muted-foreground",
                    compact && "text-[10px]"
                  )}
                >
                  {t(
                    "autoplay_always_description",
                    "Plays each time user enters this pane (useful for loops)"
                  )}
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  );
}
